import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  Mail,
  MessageSquare,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Trash2,
  Plus,
  Filter,
  ChevronLeft,
  ChevronRight,
  Activity
} from 'lucide-react';
import { tone, toneForStatus } from "components/ui/tone";
import { SelectField } from "components/ui/select-field";

interface TimelineEvent {
  id: string;
  type: 'email' | 'social' | 'conflict' | 'milestone';
  title: string;
  description?: string;
  platform?: string;
  scheduledTime: Date;
  duration?: number; // minutes
  status: 'scheduled' | 'published' | 'failed' | 'conflict' | 'draft';
  campaignId: string;
  engagement?: {
    likes: number;
    comments: number;
    shares: number;
    reach: number;
    clicks: number;
  };
  conflicts?: Array<{
    type: 'timing' | 'audience' | 'content';
    severity: 'low' | 'medium' | 'high';
    description: string;
    conflictingEventId: string;
  }>;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  assignedTo?: string;
}

interface CampaignTimelineViewProps {
  campaignId: string;
  onEventClick?: (event: TimelineEvent) => void;
  onEventEdit?: (event: TimelineEvent) => void;
  onEventDelete?: (eventId: string) => void;
  onConflictResolve?: (eventId: string, conflictId: string) => void;
}

const CampaignTimelineView: React.FC<CampaignTimelineViewProps> = ({
  campaignId,
  onEventClick,
  onEventEdit,
  onEventDelete,
  onConflictResolve
}) => {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'month'>('week');
  const [showConflictsOnly, setShowConflictsOnly] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTimelineEvents();
  }, [campaignId, selectedDate, viewMode]);

  const loadTimelineEvents = async () => {
    try {
      setLoading(true);
      
      // Mock timeline events - in real implementation, this would fetch from API
      const mockEvents: TimelineEvent[] = [
        {
          id: 'event_1',
          type: 'email',
          title: 'Product Launch Announcement',
          description: 'Initial announcement email to subscriber list',
          scheduledTime: new Date(Date.now() + 2 * 60 * 60 * 1000),
          duration: 0,
          status: 'scheduled',
          campaignId,
          engagement: {
            likes: 0,
            comments: 0,
            shares: 0,
            reach: 0,
            clicks: 0
          },
          approvalStatus: 'approved',
          assignedTo: 'marketing@company.com'
        },
        {
          id: 'event_2',
          type: 'social',
          title: 'Product Teaser Post',
          description: 'Teaser post with product images',
          platform: 'linkedin',
          scheduledTime: new Date(Date.now() + 2.5 * 60 * 60 * 1000),
          duration: 0,
          status: 'conflict',
          campaignId,
          conflicts: [
            {
              type: 'timing',
              severity: 'medium',
              description: 'Scheduled too close to email announcement (30 minutes)',
              conflictingEventId: 'event_1'
            }
          ],
          approvalStatus: 'pending',
          assignedTo: 'social@company.com'
        },
        {
          id: 'event_3',
          type: 'social',
          title: 'Behind the Scenes Video',
          description: 'Video showing product development process',
          platform: 'instagram',
          scheduledTime: new Date(Date.now() + 4 * 60 * 60 * 1000),
          duration: 0,
          status: 'scheduled',
          campaignId,
          engagement: {
            likes: 0,
            comments: 0,
            shares: 0,
            reach: 0,
            clicks: 0
          },
          approvalStatus: 'approved',
          assignedTo: 'content@company.com'
        },
        {
          id: 'event_4',
          type: 'social',
          title: 'Customer Testimonial',
          description: 'Share customer success story',
          platform: 'twitter',
          scheduledTime: new Date(Date.now() + 6 * 60 * 60 * 1000),
          duration: 0,
          status: 'draft',
          campaignId,
          approvalStatus: 'pending',
          assignedTo: 'marketing@company.com'
        },
        {
          id: 'event_5',
          type: 'milestone',
          title: 'Campaign Launch',
          description: 'Official campaign launch milestone',
          scheduledTime: new Date(Date.now() + 1 * 60 * 60 * 1000),
          duration: 0,
          status: 'scheduled',
          campaignId
        }
      ];

      setEvents(mockEvents);
    } catch (error) {
      console.error('Failed to load timeline events:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (event: TimelineEvent) => {
    switch (event.type) {
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'social':
        return <MessageSquare className="w-4 h-4" />;
      case 'conflict':
        return <AlertTriangle className="w-4 h-4" />;
      case 'milestone':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getEventColor = (event: TimelineEvent) => {
    if (event.conflicts && event.conflicts.length > 0) {
      return 'border-orange-200 dark:border-orange-800/50 bg-orange-50 dark:bg-orange-900/30';
    }
    
    switch (event.status) {
      case 'published':
        return 'border-green-200 dark:border-green-800/50 bg-green-50 dark:bg-green-900/30';
      case 'scheduled':
        return 'border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/30';
      case 'failed':
        return 'border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/30';
      case 'draft':
        return 'border-line bg-surface-hover';
      default:
        return 'border-line ';
    }
  };

  const getStatusIcon = (status: string) => {
    // Icon choice is content; its colour follows the shared status mapping.
    const ICONS: Record<string, any> = {
      published: CheckCircle,
      scheduled: Clock,
      failed: XCircle,
      conflict: AlertTriangle,
      draft: Edit,
    };
    const Icon = ICONS[status] ?? Clock;
    return <Icon className={`w-4 h-4 ${tone(toneForStatus(status)).text}`} />;
  };

  const getApprovalStatusColor = (status?: string) => {
    switch (status) {
      case 'approved':
        return 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30';
      case 'rejected':
        return 'text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30';
      case 'pending':
        return 'text-yellow-600 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30';
      default:
        return 'text-content-secondary bg-surface-sunken';
    }
  };

  const filteredEvents = events.filter(event => {
    if (showConflictsOnly && (!event.conflicts || event.conflicts.length === 0)) {
      return false;
    }
    
    if (selectedPlatforms.length > 0 && event.platform && !selectedPlatforms.includes(event.platform)) {
      return false;
    }
    
    return true;
  });

  const groupEventsByDate = (events: TimelineEvent[]) => {
    const grouped: { [date: string]: TimelineEvent[] } = {};
    
    events.forEach(event => {
      const dateKey = event.scheduledTime.toDateString();
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(event);
    });
    
    // Sort events within each date by time
    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => a.scheduledTime.getTime() - b.scheduledTime.getTime());
    });
    
    return grouped;
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    
    switch (viewMode) {
      case 'day':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
        break;
    }
    
    setSelectedDate(newDate);
  };

  const formatDateRange = () => {
    const start = new Date(selectedDate);
    const end = new Date(selectedDate);
    
    switch (viewMode) {
      case 'day':
        return selectedDate.toLocaleDateString();
      case 'week':
        start.setDate(start.getDate() - start.getDay());
        end.setDate(start.getDate() + 6);
        return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
      case 'month':
        return selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-content-secondary">Loading timeline...</span>
      </div>
    );
  }

  const groupedEvents = groupEventsByDate(filteredEvents);

  return (
    <div className="bg-surface rounded-lg shadow-sm border">
      {/* Header */}
      <div className="p-6 border-b">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-content">Campaign Timeline</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowConflictsOnly(!showConflictsOnly)}
              className={`px-3 py-1 rounded text-sm font-medium ${
                showConflictsOnly
                  ? 'bg-orange-100 text-orange-600 dark:text-orange-300'
                  : 'bg-gray-100 dark:bg-dark-700 text-content-secondary'
              }`}
            >
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              Conflicts Only
            </button>
            <SelectField
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as 'day' | 'week' | 'month')}
              className="border rounded px-3 py-1 text-sm"
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </SelectField>
          </div>
        </div>

        {/* Date Navigation */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateDate('prev')}
              className="p-1 text-content-secondary hover:text-content"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <h4 className="text-lg font-medium text-content">{formatDateRange()}</h4>
            <button
              onClick={() => navigateDate('next')}
              className="p-1 text-content-secondary hover:text-content"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <select
              multiple
              value={selectedPlatforms}
              onChange={(e) => setSelectedPlatforms(Array.from(e.target.selectedOptions, option => option.value))}
              className="border rounded px-3 py-1 text-sm"
            >
              <option value="linkedin">LinkedIn</option>
              <option value="twitter">Twitter</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
            </select>
            <button aria-label="Filter" className="p-1 text-content-secondary hover:text-content">
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Timeline Content */}
      <div className="p-6">
        {Object.keys(groupedEvents).length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="w-12 h-12 text-content-subtle mx-auto mb-4" />
            <h3 className="text-lg font-medium text-content mb-2">No Events Scheduled</h3>
            <p className="text-content-secondary mb-4">No events found for the selected time period.</p>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto">
              <Plus className="w-4 h-4" />
              Add Event
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedEvents)
              .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
              .map(([date, dateEvents]) => (
                <div key={date}>
                  <h4 className="text-sm font-medium text-content mb-4 sticky top-0 bg-surface py-2">
                    {new Date(date).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </h4>
                  
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-dark-600"></div>
                    
                    <div className="space-y-6">
                      {dateEvents.map((event, _index) => (
                        <div key={event.id} className="relative flex items-start gap-4">
                          {/* Timeline dot */}
                          <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                            event.conflicts && event.conflicts.length > 0
                              ? 'border-orange-500 bg-orange-100 dark:bg-orange-900/30'
                              : event.status === 'published'
                              ? 'border-green-500 bg-green-100 dark:bg-green-900/30'
                              : event.status === 'scheduled'
                              ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/30'
                              : 'border-line-strong '
                          }`}>
                            {getEventIcon(event)}
                          </div>
                          
                          {/* Event card */}
                          <div className={`flex-1 p-4 rounded-lg border-2 ${getEventColor(event)}`}>
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h5 className="font-medium text-content">{event.title}</h5>
                                  {getStatusIcon(event.status)}
                                  {event.platform && (
                                    <span className="px-2 py-1 bg-gray-100 dark:bg-dark-700 text-content-secondary text-xs rounded capitalize">
                                      {event.platform}
                                    </span>
                                  )}
                                  {event.approvalStatus && (
                                    <span className={`px-2 py-1 text-xs rounded ${getApprovalStatusColor(event.approvalStatus)}`}>
                                      {event.approvalStatus}
                                    </span>
                                  )}
                                </div>
                                
                                <p className="text-sm text-content-secondary mb-2">
                                  {event.scheduledTime.toLocaleTimeString([], { 
                                    hour: '2-digit', 
                                    minute: '2-digit' 
                                  })}
                                  {event.description && ` • ${event.description}`}
                                </p>
                                
                                {event.assignedTo && (
                                  <p className="text-xs text-content-muted">
                                    Assigned to: {event.assignedTo}
                                  </p>
                                )}
                              </div>
                              
                              <div className="flex items-center gap-1 ml-4">
                                <button
                                  onClick={() => onEventClick?.(event)}
                                  className="p-1 text-content-secondary hover:text-content"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => onEventEdit?.(event)}
                                  className="p-1 text-content-secondary hover:text-content"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => onEventDelete?.(event.id)}
                                  className="p-1 text-content-secondary hover:text-red-600 dark:hover:text-red-300"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Conflicts */}
                            {event.conflicts && event.conflicts.length > 0 && (
                              <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800/50 rounded">
                                <div className="flex items-center gap-2 mb-2">
                                  <AlertTriangle className="w-4 h-4 text-orange-600 dark:text-orange-300" />
                                  <span className="text-sm font-medium text-orange-800 dark:text-orange-300">
                                    {event.conflicts.length} Conflict{event.conflicts.length > 1 ? 's' : ''} Detected
                                  </span>
                                </div>
                                <div className="space-y-2">
                                  {event.conflicts.map((conflict, conflictIndex) => (
                                    <div key={conflictIndex} className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <p className="text-sm text-orange-800 dark:text-orange-300">{conflict.description}</p>
                                        <span className={`inline-block px-2 py-1 text-xs rounded mt-1 ${
                                          conflict.severity === 'high'
                                            ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-300'
                                            : conflict.severity === 'medium'
                                            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-300'
                                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-300'
                                        }`}>
                                          {conflict.severity} priority
                                        </span>
                                      </div>
                                      <button
                                        onClick={() => onConflictResolve?.(event.id, conflict.conflictingEventId)}
                                        className="ml-2 px-2 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700"
                                      >
                                        Resolve
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Engagement metrics (for published events) */}
                            {event.status === 'published' && event.engagement && (
                              <div className="mt-3 grid grid-cols-5 gap-4 p-3 bg-gray-50 dark:bg-dark-700 rounded">
                                <div className="text-center">
                                  <p className="text-sm font-medium text-content">{event.engagement.reach}</p>
                                  <p className="text-xs text-content-secondary">Reach</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-sm font-medium text-content">{event.engagement.likes}</p>
                                  <p className="text-xs text-content-secondary">Likes</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-sm font-medium text-content">{event.engagement.comments}</p>
                                  <p className="text-xs text-content-secondary">Comments</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-sm font-medium text-content">{event.engagement.shares}</p>
                                  <p className="text-xs text-content-secondary">Shares</p>
                                </div>
                                <div className="text-center">
                                  <p className="text-sm font-medium text-content">{event.engagement.clicks}</p>
                                  <p className="text-xs text-content-secondary">Clicks</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignTimelineView;